import _ from 'underscore';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { Dropdown } from '../containers';
import { A, Link } from '../base';
import { Icon } from '../ui_elements/icons';
import { fonts, animate } from '../styles/theme';
import {
  dropdownPosition,
  mobileItemColor,
  mobileItemHoverColor,
  mobileNavHeight,
  navActiveItemColor,
  navDropdownStyles,
  navHeight,
  navIconSize,
  navItemColor,
  navItemHoverBgColor,
  navItemHoverColor,
} from './theme-nav';
import { NoticeNotificationDot } from '../ui_elements';

const NavLinksList = styled('ul')`
  display: flex;
  list-style: none;
  margin: 0;
  padding-left: 0;
  @media screen and (max-width: 1000px) {
    order: ${(props) => props.mobileorder};
    flex-grow: 0;
    display: block;
  }
`;

const NavLi = styled('li')`
  position: relative;
  display: inline-flex;
  height: ${navHeight};
  margin: 0;
  &:hover {
    cursor: pointer;
  }
  @media screen and (max-width: 1000px) {
    display: block;
    height: auto;
  }
`;

const navLinkStyles = `
  display: block;
  line-height: 48px;
  transition: color ${animate.transition} ease;
  white-space: nowrap;
  padding: 0 10px;
  text-transform: none;
  &:hover {
    color: ${navItemHoverColor};
    text-decoration: none;
  }
  @media screen and (max-width: 1000px) {
    display: block;
    text-align: left;
    width: 100%;
    font-size: ${fonts.sans.size.normal};
    color: ${mobileItemColor};
    line-height: ${mobileNavHeight};
    text-transform: none;
    &:hover {
      color: ${mobileItemHoverColor};
    }
  }
`;

export const NavLink = styled(A)`
  color: ${({ isActive }) => (isActive ? navActiveItemColor : navItemColor)};
  ${navLinkStyles}
`;

const NavRouterLink = styled(Link)`
  color: ${({ isActive }) => (isActive ? navActiveItemColor : navItemColor)};
  ${navLinkStyles}
`;

export const NavIcon = styled(Icon)`
  &:before {
    width: ${navIconSize};
    font-size: ${navIconSize};
    margin: 0;
  }
  @media screen and (max-width: 1000px) {
    &:before {
      content: attr(name);
      font-family: ${fonts.sans.family};
      font-size: ${fonts.sans.size.normal};
      font-weight: ${fonts.sans.weight.bold};
      width: auto;
    }
    span {
      /* temp notification dot style */
      position: absolute;
      top: 0;
      right: 5px;
      &:after {
        box-shadow: none;
        width: 8px;
        height: 8px;
      }
    }
  }
`;

export const NavIconLink = styled(NavLink)`
  & + div {
    ${dropdownPosition.left}
  }
`;

const NavIconRouterLink = styled(NavRouterLink)`
  & + div {
    ${dropdownPosition.left}
  }
`;

const navTextLinkStyles = css`
  font-size: ${fonts.sans.size.normal};
  font-weight: ${fonts.sans.weight.bold};
  &:hover {
    font-weight: ${fonts.sans.weight.bold};
  }
  & + div > ul {
    border-top-right-radius: 2px;
  }
`;

export const NavTextLink = styled(NavLink)`
  ${navTextLinkStyles};
  &:hover {
    color: ${navItemHoverColor};
  }
`;

const NavTextRouterLink = styled(NavRouterLink)`
  ${navTextLinkStyles};
  &:hover {
    color: ${navItemHoverColor};
  }
`;

const NavLiDropdown = styled(NavLi)`
  > div {
    position: initial;
  }
  ul {
    top: 50px;
    left: 0;
  }
  &:hover {
    ${NavTextLink},
    ${NavIconLink} {
      color: ${navItemHoverColor};
      background: ${navItemHoverBgColor};
      text-decoration: none;
      @media screen and (max-width: 1000px) {
        color: ${mobileItemHoverColor};
        background: none;
      }
    }
  }
  ${NavTextLink},
  ${NavIconLink} {
    padding-right: 20px !important;
    @media screen and (max-width: 1000px) {
      color: ${mobileItemColor};
      &::after {
        position: absolute;
        top: 0;
        right: 10px;
        line-height: ${mobileNavHeight};
      }
    }
  }
`;

const NavDropdown = styled(Dropdown)`
  ${navDropdownStyles};
  display: inherit;
  &::after {
    color: ${navItemColor};
  }
  &:hover::after {
    color: ${navItemHoverColor};
  }
`;

const NavLinksCollection = (props) => {
  const { links, mobileorder } = props;

  // Takes a link and determines if it points to the current page
  // Can be deprecated in the future for React Router's NavLink component
  const linkIsActive = (link) => {
    const hrefHasPath = link.path && window.location.href.includes(link.path);
    const hrefHasChildPath =
      (link.children || []).length &&
      new RegExp(
        _.flatten(link.children)
          .filter((l) => l.path)
          .map((l) => l.path)
          .join('|')
      ).test(window.location.href);

    return hrefHasPath || hrefHasChildPath;
  };

  const renderedLinks = links.map((link) => {
    const linkProps = {
      name: link.name,
      path: link.path,
      type: link.icon,
      color: link.color || null,
    };

    // Item is an empty dropdown
    if (link.children && link.children.length === 0 && !link.path) {
      return <></>;
    }

    const linkChildren = link.noticeRequest ? <NoticeNotificationDot {...link} /> : null;

    // Should link use react-router? Check to prop
    // Should link be icon or text? Check icon prop
    let DynamicLinkComponent;
    if (link.icon) {
      DynamicLinkComponent = NavIconLink;
      if (link.to) {
        DynamicLinkComponent = NavIconRouterLink;
      }
    } else {
      DynamicLinkComponent = NavTextLink;
      if (link.to) {
        DynamicLinkComponent = NavTextRouterLink;
      }
    }

    // Every item is a link whether dropdown or not
    let linkDisplay;
    const attributes = {
      title: link.name,
      key: link.name,
      to: link.to,
      href: !(link.children && link.children.length) ? link.path : null,
    };

    if (link.icon) {
      linkDisplay = (
        <DynamicLinkComponent {...attributes}>
          <NavIcon {...linkProps}>{linkChildren}</NavIcon>
        </DynamicLinkComponent>
      );
    } else {
      attributes.key = link.name;
      attributes.isActive = linkIsActive(link);

      linkDisplay = (
        <DynamicLinkComponent {...attributes}>
          {link.name}
          {linkChildren}
        </DynamicLinkComponent>
      );
    }

    // Link is dropdown
    if ((link.children || []).length > 0) {
      const Trigger = () => linkDisplay;
      const title = link.icon ? link.name : '';

      return (
        <NavLiDropdown key={link.name} isActive={linkIsActive(link)}>
          <NavDropdown Trigger={Trigger} links={link.children} title={title} triggerArrow />
        </NavLiDropdown>
      );
    }

    return <NavLi key={link.name}>{linkDisplay}</NavLi>;
  });

  return <NavLinksList mobileorder={mobileorder}>{renderedLinks}</NavLinksList>;
};

const dropdownLinkShape = PropTypes.shape({
  name: PropTypes.string,
  path: PropTypes.string,
});

NavLinksCollection.defaultProps = {
  mobileorder: '',
};

NavLinksCollection.propTypes = {
  mobileorder: PropTypes.string,
  links: PropTypes.arrayOf(
    PropTypes.oneOfType([dropdownLinkShape, PropTypes.arrayOf(dropdownLinkShape)])
  ).isRequired,
};

export default NavLinksCollection;
